import { type EditorWidget, type Theme } from 'mini-code'
import { type LevelInfo } from '../bytecode.ts'
import { WAVEFORM_BUFFER_SIZE } from '../bytecode.ts'
import { dspscript } from '../dsp-script.ts'
import { levelPreviousValues, waveformCanvases, waveformPointersMap } from './common.ts'

export class LevelWidget {
  private widgetKey: string
  private waveformHeight = 20
  private waveformId: number
  private editorWidget: EditorWidget | null = null
  private contextId: string
  private shouldAnimate?: () => boolean

  constructor(
    private info: LevelInfo,
    private theme: Theme,
    shouldAnimate?: () => boolean,
  ) {
    this.widgetKey = `level-${info.id}-${info.line}-${info.column}`
    this.waveformId = info.id
    this.contextId = 'global'
    this.shouldAnimate = shouldAnimate
  }

  setContextId(contextId: string): void {
    this.contextId = contextId
  }

  private getWaveformKey(): string {
    return `${this.contextId}-${this.waveformId}`
  }

  toEditorWidget(): EditorWidget {
    if (this.editorWidget) return this.editorWidget

    this.editorWidget = {
      type: 'above',
      line: this.info.line,
      column: this.info.column,
      length: this.info.length,
      height: this.waveformHeight,

      render: (ctx, x, y, width, height) => {
        width -= 3
        height -= 3

        if (width <= 0 || height <= 0) {
          return
        }

        ctx.fillStyle = this.theme.background
        ctx.fillRect(x, y, width, height)

        const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
        const canvasWidth = Math.max(1, Math.floor(width * dpr))
        const canvasHeight = Math.max(1, Math.floor(height * dpr))

        const waveformKey = this.getWaveformKey()

        let canvasData = waveformCanvases.get(waveformKey)
        if (!canvasData) {
          const canvas = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(canvasWidth, canvasHeight)
            : (() => {
              const el = document.createElement('canvas')
              el.width = canvasWidth
              el.height = canvasHeight
              return el
            })()

          const offscreenCtx = canvas.getContext('2d', {
            willReadFrequently: true,
          }) as OffscreenCanvasRenderingContext2D
          if (!offscreenCtx) {
            ctx.strokeStyle = 'rgba(100, 100, 100, 0.5)'
            ctx.strokeRect(x, y, width, height)
            return
          }

          offscreenCtx.imageSmoothingEnabled = false
          offscreenCtx.scale(dpr, dpr)

          offscreenCtx.fillStyle = this.theme.background
          offscreenCtx.fillRect(0, 0, width, height)

          canvasData = { canvas, ctx: offscreenCtx }
          waveformCanvases.set(waveformKey, canvasData)
        }
        else if (canvasData.canvas.width !== canvasWidth || canvasData.canvas.height !== canvasHeight) {
          if (canvasData.canvas instanceof HTMLCanvasElement) {
            canvasData.canvas.width = canvasWidth
            canvasData.canvas.height = canvasHeight
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
            canvasData.ctx.fillStyle = this.theme.background
            canvasData.ctx.fillRect(0, 0, width, height)
          }
          else if (canvasData.canvas instanceof OffscreenCanvas) {
            const newCanvas = new OffscreenCanvas(canvasWidth, canvasHeight)
            const newCtx = newCanvas.getContext('2d', { willReadFrequently: true })
            if (newCtx) {
              newCtx.imageSmoothingEnabled = false
              newCtx.scale(dpr, dpr)
              newCtx.fillStyle = this.theme.background
              newCtx.fillRect(0, 0, width, height)
              canvasData = { canvas: newCanvas, ctx: newCtx }
              waveformCanvases.set(waveformKey, canvasData)
            }
          }
          else {
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
            canvasData.ctx.fillStyle = this.theme.background
            canvasData.ctx.fillRect(0, 0, width, height)
          }
        }

        const { canvas: offscreenCanvas, ctx: offscreenCtx } = canvasData

        const isPlayingGlobal = dspscript.isPlaying()

        if (!isPlayingGlobal) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        if (this.shouldAnimate && !this.shouldAnimate()) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const wasmMemory = dspscript.getWasmMemory()
        const pointer = waveformPointersMap.get(this.waveformId)
        const writePositions = dspscript.getWaveformWritePositions()
        if (!wasmMemory || !pointer || !writePositions) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const bufferView = new Float32Array(wasmMemory, pointer.bufferPtr, WAVEFORM_BUFFER_SIZE)
        const writePosition = Atomics.load(writePositions, this.waveformId)

        // Read all samples from buffer for level calculation
        const samplesToRead = 2048
        let rawPeak = 0

        for (let s = 0; s < samplesToRead; s++) {
          const idx = (writePosition - samplesToRead + s + WAVEFORM_BUFFER_SIZE) % WAVEFORM_BUFFER_SIZE
          const sample = bufferView[idx]!
          const absSample = Math.abs(sample)
          rawPeak = Math.max(rawPeak, absSample)
        }

        // Apply gravity fall effect
        const previousValues = levelPreviousValues.get(waveformKey)
        const fallRate = 0.92 // Decay factor per frame for bar
        const peakLineFallRate = 0.98 // Slower decay for peak line

        let peak: number
        let peakLine: number

        if (previousValues) {
          // Peak bar: fall when decreasing, rise instantly when increasing
          if (rawPeak < previousValues.peak) {
            peak = previousValues.peak * fallRate
          }
          else {
            peak = rawPeak
          }

          // Peak line: fall slower, rise instantly when increasing
          if (rawPeak < previousValues.peakLine) {
            peakLine = previousValues.peakLine * peakLineFallRate
          }
          else {
            peakLine = rawPeak
          }
        }
        else {
          peak = rawPeak
          peakLine = rawPeak
        }

        levelPreviousValues.set(waveformKey, { peak, peakLine })

        // Clear canvas
        offscreenCtx.fillStyle = this.theme.background
        offscreenCtx.fillRect(0, 0, width, height)

        // Create gradient for the bar
        const grad = offscreenCtx.createLinearGradient(0, 0, width, 0)
        grad.addColorStop(0, '#0000ff') // blue
        grad.addColorStop(0.5, '#00ff00') // green
        grad.addColorStop(0.8, '#ffff00') // yellow
        grad.addColorStop(0.95, '#ffa500') // orange
        grad.addColorStop(1, '#ff0000') // red

        // Draw peak filled bar
        if (Number.isFinite(peak) && peak > 0) {
          const peakWidth = Math.max(0, Math.min(width, peak * width))
          offscreenCtx.fillStyle = grad
          offscreenCtx.fillRect(0, 0, peakWidth, height)
        }

        // Draw peak line
        if (Number.isFinite(peakLine) && peakLine > 0) {
          const peakLineX = Math.max(0, Math.min(width, peakLine * width))
          offscreenCtx.strokeStyle = grad
          offscreenCtx.lineWidth = 1
          offscreenCtx.beginPath()
          offscreenCtx.moveTo(peakLineX, 0)
          offscreenCtx.lineTo(peakLineX, height)
          offscreenCtx.stroke()
        }

        ctx.drawImage(offscreenCanvas, x, y, width, height)
      },
    }

    return this.editorWidget
  }
}
